import classNames from 'classnames'
import type { ChangeEvent, ForwardedRef } from 'react'
import React, {
  forwardRef,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { FormLabel } from './form/FormLabel'
import { FormError } from './form/FormError'
import type { FormFieldDefaults } from './types'
import { useTranslationTyped } from 'i18n/i18n'

export interface BreakoutInputProps extends FormFieldDefaults<'input'> {
  // secondary is the default 'kind' and represents the "primary" style in figma
  // tertiary is the secondary style in figma
  kind?: 'secondary' | 'tertiary'
  sublabel?: string
  // an icon element or a React component that renders an icon
  LeadingIcon?:
    | React.ComponentType<{ size: number } & React.SVGProps<SVGSVGElement>>
    | React.ReactNode
  renderUnderneath?: React.ReactNode
  TrailingIcon?:
    | React.ComponentType<{ size: number } & React.SVGProps<SVGSVGElement>>
    | React.ReactNode
  iconClassName?: string
  inputClassName?: string
  allowClear?: boolean
  iconSize?: number
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  preventEditingValue?: boolean
  /** Sizing preset. 'compact' = 32px tall with an 8px (rounded-lg) radius;
   * 'default' = 52px tall with a 16px (rounded-2xl) radius. */
  variant?: 'default' | 'compact'
  autoFocus?: boolean
  showCharacterCount?: boolean
  showErrorOutline?: boolean
}

export const BreakoutTextInput = forwardRef(function BreakoutTextInput(
  {
    disabled,
    kind = 'tertiary',
    name,
    label,
    labelClass,
    sublabel,
    defaultValue,
    className,
    testId,
    LeadingIcon,
    TrailingIcon,
    renderUnderneath,
    iconSize = 18,
    iconClassName,
    required,
    placeholder,
    error,
    errorClass,
    inputClassName,
    allowClear,
    preventEditingValue,
    variant = 'default',
    autoFocus,
    showCharacterCount,
    showErrorOutline,
    ...rest
  }: BreakoutInputProps,
  ref: ForwardedRef<HTMLInputElement>
) {
  const isCompact = variant === 'compact'

  const { tt } = useTranslationTyped()
  const id = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue || '')

  const canEditValue = !preventEditingValue && !disabled
  const value = rest.value ?? uncontrolledValue

  useEffect(() => {
    const input = inputRef.current
    if (input) {
      const form = input.form
      if (!form) return
      const listener = () => {
        setUncontrolledValue('')
      }
      form.addEventListener('reset', listener)

      return () => {
        form.removeEventListener('reset', listener)
      }
    }
  }, [rest.value])

  return (
    <div className={classNames('flex flex-col items-start gap-1', className)}>
      <FormLabel
        variant={variant}
        label={label}
        labelClass={labelClass}
        for={id}
        required={required}
      />
      {sublabel && (
        <FormLabel
          variant={variant}
          label={sublabel}
          labelClass="text-body-small"
          for={id}
        />
      )}

      <div className="relative flex w-full items-center">
        <RenderLeadingIcon
          LeadingIcon={LeadingIcon}
          iconSize={iconSize}
          iconClassName={`${iconClassName || ''} absolute left-3 top-1/2 -translate-y-1/2 transform`}
        />
        <input
          ref={ref || inputRef}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          data-testid={testId || `${name ? name + '-' : ''}input`}
          tabIndex={0}
          name={name}
          id={id}
          disabled={!canEditValue}
          defaultValue={defaultValue || undefined}
          placeholder={placeholder}
          className={classNames(
            'flex w-full flex-row items-center text-label-medium',

            // compact = 32px tall, 8px radius; default = 52px, 16px radius
            isCompact && 'h-8 rounded-lg px-3 py-2',
            !isCompact && 'h-[3.25rem] rounded-2xl px-4 py-1',

            'justify-between outline outline-1 outline-outline-light focus:text-base md:focus:text-xs',
            inputClassName,
            {
              'bg-core-tertiary': kind === 'tertiary' && canEditValue,
              'bg-core-secondary': kind === 'secondary' && canEditValue,
              'bg-surface-dim': kind === 'secondary' && disabled,
              'bg-surface': kind === 'tertiary' && disabled,
              'text-on-surface-disabled': disabled,
              'pl-9': LeadingIcon,
              'pr-9': TrailingIcon,
              'focus-ring-edge': !disabled,
              // On error the focus ring adopts the error color so the red
              // wins visually while keeping focus visible (WCAG 2.4.7).
              'border border-core-error outline-core-error focus-visible:!outline-core-error':
                !!error || showErrorOutline,
              'cursor-not-allowed': !canEditValue,
            }
          )}
          autoFocus={autoFocus}
          {...rest}
          onChange={(e) => {
            if (rest.onChange) rest.onChange(e)
            if (rest.value === undefined) {
              setUncontrolledValue(e.target.value)
            }
          }}
        />
        <RenderTrailingIcon
          TrailingIcon={TrailingIcon}
          iconSize={iconSize}
          iconClassName={`${iconClassName || ''} absolute right-3 top-1/2 -translate-y-1/2 transform`}
        />
        {allowClear && value && !disabled && (
          <button
            className="absolute right-3 cursor-pointer p-2"
            data-testid={`datetime-clear-${name}`}
            onClick={() => {
              if (inputRef.current) inputRef.current.value = ''
              const event = { currentTarget: inputRef.current }
              if (rest.value === undefined) {
                setUncontrolledValue('')
              }
              rest.onChange?.({
                target: inputRef.current,
                currentTarget: inputRef.current,
              } as ChangeEvent<HTMLInputElement>)
              rest.onKeyDown?.(event as React.KeyboardEvent<HTMLInputElement>)
              rest.onKeyUp?.(event as React.KeyboardEvent<HTMLInputElement>)
            }}
          >
            <strong className="text-body-medium lowercase text-on-surface-var hover:text-core-error">
              {tt.inputs.clear()}
            </strong>
          </button>
        )}
      </div>
      {rest.maxLength && showCharacterCount ? (
        <div className="flex w-full justify-end gap-2.5 text-body-small">
          {renderUnderneath}
          <span className="pr-2 text-right text-on-surface-var">
            {value?.length || 0}/{rest.maxLength}
          </span>
        </div>
      ) : null}
      <FormError error={error} errorClass={errorClass} id={`${id}-error`} />
    </div>
  )
})

const RenderLeadingIcon = ({
  LeadingIcon,
  iconSize,
  iconClassName,
}: {
  LeadingIcon: BreakoutInputProps['LeadingIcon']
  iconSize: BreakoutInputProps['iconSize']
  iconClassName: BreakoutInputProps['iconClassName']
}) => {
  if (!LeadingIcon) return null

  if (isValidElement(LeadingIcon)) return LeadingIcon

  return (
    // @ts-expect-error - LeadingIcon is a React component
    <LeadingIcon
      size={iconSize}
      className={`${iconClassName || ''} absolute left-3 top-1/2 -translate-y-1/2 transform`}
    />
  )
}

const RenderTrailingIcon = ({
  TrailingIcon,
  iconSize,
  iconClassName,
}: {
  TrailingIcon: BreakoutInputProps['TrailingIcon']
  iconSize: BreakoutInputProps['iconSize']
  iconClassName: BreakoutInputProps['iconClassName']
}) => {
  if (!TrailingIcon) return null

  if (isValidElement(TrailingIcon)) return TrailingIcon

  return (
    // @ts-expect-error - TrailingIcon is a React component
    <TrailingIcon
      size={iconSize}
      className={`${iconClassName || ''} absolute right-3 top-1/2 -translate-y-1/2 transform`}
    />
  )
}
